import { CheckmarkCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useAccountInfo,
  useRequestMultiFactorAuth,
  useResetMultiFactorAuth,
} from 'src/api/queries';
import { Button, NotificationBox, Tooltip } from 'src/components';

import { Select, Trans, useLingui } from '@lingui/react/macro';
import { DisableMfaModal } from './DisableMfaModal';

export const EditMultiFactorAuth = () => {
  const { t } = useLingui();
  const accountInfo = useAccountInfo();
  const requestMfa = useRequestMultiFactorAuth();
  const resetMfa = useResetMultiFactorAuth();

  if (accountInfo.isPending) return null;

  const hasMfa = accountInfo.data?.mfaEnrolled === true;

  const handleRequestMfaClick = () => {
    requestMfa.mutate();
    tracker.trackEvent(
      new ButtonClicked({
        component_location: ComponentLocations.ACCOUNT.SECURITY_CARD,
        text: 'Enable MFA',
        action: 'enable MFA',
      })
    );
  };

  return (
    <>
      <div>
        <div className="flex justify-between gap-16">
          <Typography variant="heading-subtle-lg" as="h3">
            <Select
              value={hasMfa || accountInfo.isError ? 'edit' : 'add'}
              _add="Add multi-factor authentication"
              other="Multi-factor authentication"
            />
            {hasMfa && (
              <Tooltip title={t`MFA is enabled`}>
                <CheckmarkCircle
                  size={24}
                  className="mt-[-7px] ml-8 inline-block text-feedback-green-500"
                />
              </Tooltip>
            )}
          </Typography>
        </div>

        <div className="flex flex-col gap-16 lm:flex-row">
          <Typography variant="body-md">
            <Trans>
              Keep your account secure with an extra verification step. With MFA
              you&apos;ll be asked to provide a second authentication factor
              when logging in.
            </Trans>
          </Typography>

          {!requestMfa.isSuccess &&
            !accountInfo.isError &&
            (hasMfa ? (
              <DisableMfaModal
                trigger={
                  <Button
                    variant="tertiary"
                    size="medium"
                    className="w-full self-start whitespace-nowrap ls:w-fit"
                    data-cy="reset-mfa-button"
                  >
                    <span className="whitespace-nowrap">
                      <Trans>Disable MFA</Trans>
                    </span>
                  </Button>
                }
              />
            ) : (
              <Button
                variant="tertiary"
                size="medium"
                className="w-full self-start whitespace-nowrap ls:w-fit"
                data-cy="enroll-mfa-button"
                disabled={requestMfa.isPending}
                loading={requestMfa.isPending}
                onClick={handleRequestMfaClick}
              >
                <span className="whitespace-nowrap">
                  <Trans>Enable MFA</Trans>
                </span>
              </Button>
            ))}
        </div>
      </div>
      {requestMfa.isSuccess && (
        <NotificationBox
          variant="success"
          title={<Trans>Email sent</Trans>}
          data-cy="enroll-mfa-success-notification"
        >
          <Typography variant="body-md">
            <Trans>
              We sent you an email with instructions to activate your
              multi-factor authentication.
            </Trans>
          </Typography>
        </NotificationBox>
      )}
      {(requestMfa.isError || resetMfa.isError) && (
        <NotificationBox
          error={[requestMfa.error, resetMfa.error]}
          data-cy="update-mfa-failure-notification"
        >
          <Typography variant="body-sm" as="p">
            <Select
              value={requestMfa.isError ? 'enable' : 'disable'}
              _enable="Something went wrong while enabling multi-factor authentication. Please try again later."
              other="Something went wrong while disabling multi-factor authentication. Please try again later."
            />
          </Typography>
        </NotificationBox>
      )}
      {accountInfo.isError && (
        <NotificationBox error={accountInfo.error}>
          <Trans>
            We couldn&apos;t load your multi-factor authentication status.
            Please try again later.
          </Trans>
        </NotificationBox>
      )}
    </>
  );
};
